/**
 * 🔗 ตั้งค่าการตอบรวม — ให้บอท "ตอบทุกเรื่องในคำถามเดียว"
 * ลูกค้าถามหลายเรื่องในประโยคเดียว (มีสีแดงไหม + อก/เอว size อะไร + ขอดูรูป)
 * → บอทรวมตอบทุกเรื่อง แทนเลือกตอบเรื่องเดียว. admin เปิด/ปิด + เรียงลำดับเรื่องเองได้
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { ComposeConfigApi } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBrands, brandEmoji, brandLabel } from '@/lib/useBrands';

const KNOWN = ['price', 'color', 'stock', 'size', 'faq'] as const;
type Topic = (typeof KNOWN)[number];
const TOPIC_LABEL: Record<Topic, string> = {
  price: '💰 ราคา (ดึงจากที่ตั้งในเมนูราคา)',
  color: '🎨 สี (มี/หมด + เสนอสีอื่น)',
  stock: '📦 สต๊อก สี × ไซซ์ (เช็คทุกไซซ์ที่ถาม เช่น "ฟ้า M กับ L")',
  size: '📏 แนะนำไซซ์จากสัดส่วน (อก/เอว/สะโพก)',
  faq: '💬 FAQ ที่ตั้งไว้เอง (ตอบได้หลายข้อพร้อมกัน — ไม่จำกัด)',
};

/** normalize topic_order → เฉพาะ topic ที่รู้จัก + เติมที่ขาดต่อท้าย */
function normalizeOrder(raw: string | undefined): Topic[] {
  const parts = (raw ?? '').split(',').map((s) => s.trim());
  const seen = parts.filter((p): p is Topic => (KNOWN as readonly string[]).includes(p));
  for (const k of KNOWN) if (!seen.includes(k)) seen.push(k);
  return seen;
}

export default function ComposeSettingsPage() {
  const qc = useQueryClient();
  const { keys } = useBrands();
  const [brand, setBrand] = useState<string>('');
  useEffect(() => {
    if (!brand && keys.length) setBrand(keys[0]!);
  }, [keys, brand]);

  const cfgQ = useQuery({
    queryKey: ['compose-config', brand],
    queryFn: () => ComposeConfigApi.get(brand),
    enabled: !!brand,
  });

  const [enabled, setEnabled] = useState(true);
  const [maxTopics, setMaxTopics] = useState(4);
  const [order, setOrder] = useState<Topic[]>([...KNOWN]);
  const [on, setOn] = useState<Set<Topic>>(new Set(KNOWN)); // เรื่องที่ติ๊กให้นำมารวม

  useEffect(() => {
    if (cfgQ.data) {
      setEnabled(!!cfgQ.data.combine_enabled);
      setMaxTopics(cfgQ.data.max_topics || 4);
      const picked = (cfgQ.data.topic_order ?? '')
        .split(',').map((s) => s.trim())
        .filter((p): p is Topic => (KNOWN as readonly string[]).includes(p));
      setOn(new Set(picked.length ? picked : KNOWN));
      setOrder(normalizeOrder(cfgQ.data.topic_order));
    }
  }, [cfgQ.data]);

  const toggle = (t: Topic) => setOn((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });

  const saveMut = useMutation({
    mutationFn: () =>
      ComposeConfigApi.save({ brand, combineEnabled: enabled, maxTopics, topicOrder: order.filter((t) => on.has(t)).join(',') }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compose-config', brand] });
      toast.success('บันทึกแล้ว ✅');
    },
    onError: (e) => toast.error(`ผิดพลาด: ${(e as Error).message}`),
  });

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setOrder(next);
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">🔗 ตั้งค่าการตอบรวม</h2>
        <p className="text-sm text-muted-foreground">
          ให้บอท <b>ตอบทุกเรื่องในคำถามเดียว</b> — เช่น ลูกค้าถาม "มีสีแดงไหม อก 40 เอว 34 ใส่ไซซ์อะไร"
          บอทจะตอบทั้ง <b>สี + ไซซ์</b> รวมกัน (เดิมเลือกตอบเรื่องเดียว) · รูปภาพส่งแยกอัตโนมัติเหมือนเดิม
        </p>
      </div>

      {/* เลือกแบรนด์ */}
      <div className="flex flex-wrap gap-1.5">
        {keys.map((k) => (
          <button
            key={k}
            onClick={() => setBrand(k)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              brand === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-accent'
            }`}
          >
            {brandEmoji(k)} {brandLabel(k)}
          </button>
        ))}
      </div>

      {cfgQ.isLoading ? (
        <div className="text-sm text-muted-foreground py-6">กำลังโหลด…</div>
      ) : (
        <div className="space-y-4 border rounded-lg p-4 bg-card">
          {/* เปิด/ปิด */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="mt-1 h-4 w-4" />
            <span>
              <span className="font-medium">เปิดโหมดตอบรวม</span>
              <span className="block text-xs text-muted-foreground">
                ปิด = บอทตอบเรื่องเดียว (เลือกตามลำดับด้านล่าง) เหมือนเดิม
              </span>
            </span>
          </label>

          {/* จำนวนเรื่องสูงสุด */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium w-40">จำนวนเรื่องสูงสุด/คำตอบ</span>
            <Input
              type="number"
              min={1}
              max={10}
              value={maxTopics}
              onChange={(e) => setMaxTopics(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
              className="w-20 h-8"
              disabled={!enabled}
            />
            <span className="text-xs text-muted-foreground">กันคำตอบยาวเกิน</span>
          </div>

          {/* เลือกเรื่องที่จะนำมารวม + ลำดับ */}
          <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
            <div className="text-sm font-medium mb-1">เลือกเรื่องที่ให้บอทตอบรวม (ติ๊ก = รวม) + เรียงลำดับ</div>
            <div className="space-y-1.5">
              {order.map((t, i) => {
                const checked = on.has(t);
                return (
                  <div key={t} className={`flex items-center gap-2 border rounded-md px-3 py-2 ${checked ? 'bg-background' : 'bg-muted/40 opacity-60'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(t)} className="h-4 w-4 shrink-0" />
                    <span className="text-xs text-muted-foreground w-5">{checked ? order.filter((x) => on.has(x)).indexOf(t) + 1 : '—'}</span>
                    <span className="flex-1 text-sm">{TOPIC_LABEL[t]}</span>
                    <button onClick={() => move(i, -1)} disabled={i === 0 || !checked} className="p-1 disabled:opacity-30 hover:bg-accent rounded">
                      <ArrowUp size={15} />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === order.length - 1 || !checked} className="p-1 disabled:opacity-30 hover:bg-accent rounded">
                      <ArrowDown size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              ติ๊กออก = ไม่นำเรื่องนั้นมารวม · "จำนวนเรื่องสูงสุด" ด้านบน = ตอบได้มากสุดกี่เรื่องต่อ 1 คำตอบ
            </p>
          </div>

          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !brand} className="gap-2">
            <Save size={16} /> {saveMut.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            💡 คำเชื่อม/คำลงท้ายของคำตอบรวม แก้ได้ที่ <b>คำตอบบอท → เทมเพลต</b> (key <code>compose_reply</code>)
          </p>
        </div>
      )}
    </div>
  );
}
